

import { useMemo, useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { Banner, Button, buttonClass, Drawer, Field, Input, Modal, Select, TextArea, useToast } from "@/shared/rd";
import type { PaymentReviewRejectReasonCode, SellerOrderDetail } from "@/lib/api/types";
import { formatAmountValue } from "./order-list.utils";
import { PaymentReviewProofViewer } from "./PaymentReviewProofViewer";
import { usePaymentReviewState } from "./usePaymentReviewState";

type Props = {
  locale: string;
  storeId: string;
  orderId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (order: SellerOrderDetail) => void;
  openOrderHref?: string;
};

function mapErrorMessage(
  code: string | null,
  t: ReturnType<typeof useTranslations>,
): string | null {
  if (!code) {
    return null;
  }
  if (code === "STALE_STATUS") {
    return t(keys.orders.paymentReviewConflictNotice);
  }
  if (code === "PAYMENT_PROOF_REQUIRED") {
    return t(keys.orders.paymentReviewProofRequired);
  }
  if (code === "INVALID_APPROVED_AMOUNT") {
    return t(keys.orders.approvedAmountInvalid);
  }
  if (code === "PAYMENT_REJECT_REASON_REQUIRED") {
    return t(keys.orders.reviewRejectReasonRequired);
  }
  if (code === "PAYMENT_REVIEW_LOAD_ERROR") {
    return t(keys.orders.paymentReviewLoadError);
  }
  return t(keys.errors.generic);
}

function getDifferenceMeta(
  difference: number,
  t: ReturnType<typeof useTranslations>,
) {
  if (difference < 0) {
    return t(keys.orders.differenceUnderpaid);
  }
  if (difference > 0) {
    return t(keys.orders.differenceOverpaid);
  }
  return t(keys.orders.differenceExact);
}

export function PaymentReviewDialog({
  locale,
  storeId,
  orderId,
  isOpen,
  onClose,
  onSuccess,
  openOrderHref,
}: Props) {
  const t = useTranslations();
  const toast = useToast();
  const [lastSubmittedAction, setLastSubmittedAction] = useState<"approve" | "reject">(
    "approve",
  );
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  const state = usePaymentReviewState({
    isOpen,
    storeId,
    orderId,
    onSuccess: (updatedOrder) => {
      toast.show({
        message:
          lastSubmittedAction === "reject"
            ? t(keys.orders.paymentRejectedNotice)
            : t(keys.orders.paymentApprovedNotice),
      });
      onSuccess(updatedOrder);
      setShowApproveConfirm(false);
      setShowRejectConfirm(false);
      onClose();
    },
  });

  // Reset local state on dialog open/close (adjust-state-during-render
  // pattern instead of an effect).
  const isActive = Boolean(isOpen && orderId);
  const [wasActive, setWasActive] = useState(isActive);
  if (wasActive !== isActive) {
    setWasActive(isActive);
    if (!isActive) {
      setLastSubmittedAction("approve");
      setShowApproveConfirm(false);
      setShowRejectConfirm(false);
      setShowDiscardConfirm(false);
    }
  }

  const requestClose = () => {
    if (state.isSubmitting) {
      return;
    }
    if (state.hasUnsavedChanges) {
      setShowDiscardConfirm(true);
      return;
    }
    onClose();
  };

  const difference = state.buyerEnteredAmount - state.requiredAmount;
  const differenceLabel = getDifferenceMeta(difference, t);
  // A missing amount or reason is said under its field; anything else that
  // failed is a banner at the top of the drawer.
  const approvedAmountError =
    state.errorCode === "INVALID_APPROVED_AMOUNT" ? t(keys.orders.approvedAmountInvalid) : undefined;
  const reasonError =
    state.errorCode === "PAYMENT_REJECT_REASON_REQUIRED" ? t(keys.orders.reviewRejectReasonRequired) : undefined;
  const errorMessage =
    approvedAmountError || reasonError || state.errorCode === "STALE_STATUS"
      ? null
      : mapErrorMessage(state.errorCode, t);
  const failedTitle =
    state.errorCode === "PAYMENT_PROOF_REQUIRED" || state.errorCode === "PAYMENT_REVIEW_LOAD_ERROR"
      ? undefined
      : t(lastSubmittedAction === "reject" ? keys.orders.paymentReviewRejectError : keys.orders.paymentReviewApproveError);

  const rejectOptions = useMemo(
    () =>
      [
        ["amount_mismatch", keys.orders.reviewRejectAmountMismatch],
        ["proof_unclear", keys.orders.reviewRejectProofUnclear],
        ["wrong_proof", keys.orders.reviewRejectWrongProof],
        ["suspected_fake", keys.orders.reviewRejectSuspectedFake],
        ["other", keys.orders.reviewRejectOther],
      ] as Array<[PaymentReviewRejectReasonCode, string]>,
    [],
  );
  const rejectReasonLabelByCode = useMemo(
    () =>
      Object.fromEntries(rejectOptions) as Record<PaymentReviewRejectReasonCode, string>,
    [rejectOptions],
  );

  const handleApprove = () => {
    setLastSubmittedAction("approve");
    if (!state.hasProof) {
      state.setErrorCode("PAYMENT_PROOF_REQUIRED");
      return;
    }
    if (!state.hasValidApprovedAmount) {
      state.setErrorCode("INVALID_APPROVED_AMOUNT");
      return;
    }
    setShowApproveConfirm(true);
  };

  const handleReject = () => {
    setLastSubmittedAction("reject");
    if (!state.reasonCode) {
      state.setErrorCode("PAYMENT_REJECT_REASON_REQUIRED");
      return;
    }
    setShowRejectConfirm(true);
  };

  const confirmOpen = showApproveConfirm || showRejectConfirm || showDiscardConfirm;

  return (
    <>
      <Drawer
        open={isActive}
        onClose={confirmOpen ? undefined : requestClose}
        closeLabel={t(keys.common.close)}
        badge="bill"
        width={580}
        title={t(keys.orders.reviewPaymentTitle)}
        sub={state.order ? `${state.order.customer.name} · ${state.order.orderCode}` : undefined}
        actions={
          <>
            {openOrderHref ? (
              <a href={openOrderHref} className={buttonClass("ghost", "sm")} style={{ textDecoration: "none" }}>
                <span>{t(keys.orders.openOrderAction)}</span>
              </a>
            ) : null}
            <Button
              variant="secondary"
              size="sm"
              onClick={handleReject}
              disabled={state.isSubmitting || state.isLoading || state.isConflict}
            >
              {t(keys.orders.rejectPaymentAction)}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleApprove}
              disabled={state.isSubmitting || state.isLoading || state.isConflict || !state.hasProof}
            >
              {t(keys.orders.approvePaymentAction)}
            </Button>
          </>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {state.isConflict ? (
            <Banner tone="warning" title={t(keys.orders.paymentReviewConflictNotice)}>
              {t(keys.orders.paymentReviewOpenOrderHint)}
            </Banner>
          ) : null}
          {errorMessage ? (
            <Banner tone="error" title={failedTitle}>
              {errorMessage}
            </Banner>
          ) : null}

          {state.isLoading ? (
            <p role="status" style={{ margin: 0, fontSize: 14, color: "var(--rd-text-secondary)" }}>
              {t(keys.common.loading)}
            </p>
          ) : state.order ? (
            <>
              <section className="rd-card" style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 12 }}>
                <dl className="rd-kv" style={{ gridTemplateColumns: "minmax(0,1fr) auto" }}>
                  <dt>{t(keys.orders.requiredAmountLabel)}</dt>
                  <dd className="rd-num" style={{ textAlign: "end" }}>
                    <bdi>{formatAmountValue(locale, state.requiredAmount)}</bdi>
                  </dd>
                  <dt>{t(keys.orders.buyerEnteredAmountLabel)}</dt>
                  <dd className="rd-num" style={{ textAlign: "end" }}>
                    <bdi>{formatAmountValue(locale, state.buyerEnteredAmount)}</bdi>
                  </dd>
                  <dt style={{ color: "var(--rd-text)", fontWeight: 600 }}>{t(keys.orders.differenceAmountLabel)}</dt>
                  <dd
                    className="rd-num"
                    style={{
                      textAlign: "end",
                      fontWeight: 600,
                      color: difference < 0 ? "var(--rd-red)" : "var(--rd-text)",
                    }}
                  >
                    <bdi>
                      {formatAmountValue(locale, difference)} ({differenceLabel})
                    </bdi>
                  </dd>
                </dl>
                <Field label={t(keys.orders.approvedAmountLabel)} htmlFor="payment-review-approved" error={approvedAmountError}>
                  <Input
                    id="payment-review-approved"
                    type="number"
                    name="approvedAmount"
                    min={0}
                    step="0.01"
                    value={state.approvedAmountInput}
                    invalid={Boolean(approvedAmountError)}
                    onChange={(event) => state.setApprovedAmountInput(event.target.value)}
                    disabled={state.isConflict}
                    style={{ maxWidth: 220 }}
                  />
                </Field>
              </section>

              <PaymentReviewProofViewer
                locale={locale}
                proofItems={state.proofItems}
                selectedIndex={state.selectedProofIndex}
                onSelect={state.setSelectedProofIndex}
              />

              <section style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <Field label={t(keys.orders.reviewRejectReasonLabel)} htmlFor="payment-review-reason" error={reasonError}>
                  <Select
                    id="payment-review-reason"
                    placeholder={t(keys.orders.declineReasonPlaceholder)}
                    options={rejectOptions.map(([value, labelKey]) => ({ value, label: t(labelKey) }))}
                    value={state.reasonCode}
                    onChange={(event) =>
                      state.setReasonCode(event.target.value as PaymentReviewRejectReasonCode | "")
                    }
                  />
                </Field>
                <Field label={t(keys.orders.declineNoteLabel)} htmlFor="payment-review-note">
                  <TextArea
                    id="payment-review-note"
                    value={state.note}
                    onChange={(event) => state.setNote(event.target.value)}
                    disabled={state.isConflict}
                  />
                </Field>
              </section>
            </>
          ) : null}
        </div>
      </Drawer>

      <Modal
        open={showApproveConfirm}
        onClose={state.isSubmitting ? undefined : () => setShowApproveConfirm(false)}
        title={t(keys.orders.confirmApproveTitle)}
        actions={
          <>
            <Button variant="ghost" muted disabled={state.isSubmitting} onClick={() => setShowApproveConfirm(false)}>
              {t(keys.common.back)}
            </Button>
            <Button
              variant="primary"
              loading={state.isSubmitting}
              loadingLabel={t("orders.approving")}
              onClick={() => {
                void state.submitApprove().then((ok) => {
                  if (!ok) setShowApproveConfirm(false);
                });
              }}
            >
              {t(keys.common.submit)}
            </Button>
          </>
        }
      >
        <p className="rd-modal-text" style={{ margin: 0 }}>
          {t(keys.orders.confirmApproveBody, {
            required: formatAmountValue(locale, state.requiredAmount),
            buyer: formatAmountValue(locale, state.buyerEnteredAmount),
            approved: formatAmountValue(locale, state.approvedAmount || 0),
          })}
        </p>
      </Modal>

      <Modal
        open={showRejectConfirm}
        onClose={state.isSubmitting ? undefined : () => setShowRejectConfirm(false)}
        title={t(keys.orders.confirmRejectTitle)}
        actions={
          <>
            <Button variant="ghost" muted disabled={state.isSubmitting} onClick={() => setShowRejectConfirm(false)}>
              {t(keys.common.back)}
            </Button>
            <Button
              variant="danger"
              loading={state.isSubmitting}
              loadingLabel={t("orders.rejecting")}
              onClick={() => {
                void state.submitReject().then((ok) => {
                  if (!ok) setShowRejectConfirm(false);
                });
              }}
            >
              {t(keys.common.submit)}
            </Button>
          </>
        }
      >
        <p className="rd-modal-text" style={{ margin: 0 }}>
          {t(keys.orders.confirmRejectBody, {
            reason:
              state.reasonCode && rejectReasonLabelByCode[state.reasonCode]
                ? t(rejectReasonLabelByCode[state.reasonCode])
                : t(keys.common.empty),
          })}
        </p>
      </Modal>

      <Modal
        open={showDiscardConfirm}
        onClose={() => setShowDiscardConfirm(false)}
        title={t(keys.forms.discardChangesTitle)}
        actions={
          <>
            <Button variant="ghost" muted onClick={() => setShowDiscardConfirm(false)}>
              {t(keys.forms.discardChangesContinue)}
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                setShowDiscardConfirm(false);
                onClose();
              }}
            >
              {t(keys.forms.discardChangesDiscard)}
            </Button>
          </>
        }
      >
        <p className="rd-modal-text" style={{ margin: 0 }}>
          {t(keys.forms.discardChangesBody)}
        </p>
      </Modal>
    </>
  );
}
